import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { OrderDto } from '@tikkas/shared';
import { IC, Svg } from '../art/icons.js';
import { qrSvg } from '../lib/qr.js';

/**
 * A guest's pickup code (docs/30): the same centred white pop-up as a member's, but the QR carries the order's 4-letter
 * code (what the backup code is for members), so the till's and the kitchen's scanners find the order with no customer
 * behind it and hand it over. The letters sit under the QR for a till that types them.
 */
export function GuestCodeSheet({ order, onClose }: { order: OrderDto; onClose: () => void }) {
  const touch = useRef<{ y: number; t: number } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="qrsheet" onClick={onClose}>
      <div
        className="qrpop"
        role="dialog"
        aria-label="Your pickup code for the till"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { const t = e.touches[0]; touch.current = t ? { y: t.clientY, t: Date.now() } : null; }}
        onTouchEnd={(e) => { const st = touch.current, t = e.changedTouches[0]; touch.current = null; if (st && t && t.clientY - st.y > 70 && Date.now() - st.t < 600) onClose(); }}
      >
        <button type="button" className="qrsheet-close" aria-label="Close" onClick={onClose}><Svg html={IC.close} /></button>
        <div className="qrsheet-qr" role="img" aria-label={`Pickup code ${order.backupCode}`}><Svg html={qrSvg(order.backupCode)} /></div>
        <b className="qrsheet-name">{order.guest?.name || 'Your order'} · #{order.number}</b>
        <span className="qrsheet-sub">Show this at the till</span>
        <span className="qrsheet-backup">Or say the code <b>{order.backupCode}</b></span>
      </div>
    </div>,
    document.body,
  );
}
